import React from "react";
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Tooltip,
  Avatar,
  Paper,
  TableContainer,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Block as BlockIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { Customer } from "../../../validation/customer.validation";
import ConfirmDeleteDialog from "../../../common/confirmDeleteAlert";
import ConfirmBlockDialog from "../../../common/confirmBlockAlert";
import {useNavigate} from "react-router-dom";
import {ROLES} from "../../../constants/general.constant";
import { formatPhoneNumber } from "../../../helper/phoneFormatter.helper";

interface Props {
  customers: Customer[];
  onDelete: (emp: Customer) => void;
  onEdit: (emp: Customer) => void;
  onBlock: (emp: Customer) => void;
  user?: any;
}

const CustomerTableView: React.FC<Props> = ({ customers, onDelete, onEdit, onBlock, user = null }) => {
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = React.useState(false);
  const [openStatusDialog, setOpenStatusDialog] = React.useState(false);
  const [selectedCustomer, setSelectedCustomer] = React.useState<Customer | null>(null);
  const handleOpenDialog = (emp: Customer) => {
    setSelectedCustomer(emp);
    setOpenDialog(true);
  };

  const handleDelete = () => {
    if (selectedCustomer) {
      onDelete(selectedCustomer);
    }
    setOpenDialog(false);
    setSelectedCustomer(null);
  };

  const handleEdit = (emp: Customer) => {
    onEdit(emp);
  };

  const openDeactivateDialog = (emp: Customer) => {
    setSelectedCustomer(emp);
    setOpenStatusDialog(true);
  };

  const handleStatusConfirm = () => {
    if (selectedCustomer) {
      onBlock(selectedCustomer);
    }
    setOpenStatusDialog(false);
    setSelectedCustomer(null);
  };

  return (
    <>
      <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
        <Table sx={{ borderCollapse: "collapse" }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f9fafb" }}>
              <TableCell
                sx={{ fontWeight: "bold", border: "1px solid #e5e7eb" }}
              >
                Name
              </TableCell>
              <TableCell
                sx={{ fontWeight: "bold", border: "1px solid #e5e7eb" }}
              >
                Email
              </TableCell>
              <TableCell
                sx={{ fontWeight: "bold", border: "1px solid #e5e7eb" }}
              >
                Phone
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: "bold",
                  border: "1px solid #e5e7eb",
                  textAlign: "center",
                }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {customers.map((emp, index) => (
              <TableRow
                key={index}
                hover
                className={emp.isActive === false ? "bg-gray-100" : ""}
              >
                <TableCell
                  sx={{
                    border: "1px solid #e5e7eb",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: "#f3f4f6",
                      color: "#6b7280",
                      width: 32,
                      height: 32,
                    }}
                  >
                    <PersonIcon fontSize="small" />
                  </Avatar>
                  {`${(emp as any).firstName || ''} ${(emp as any).middleName || ''} ${(emp as any).lastName || ''}`.trim() || (emp as any).name || "-"}
                </TableCell>
                <TableCell sx={{ border: "1px solid #e5e7eb" }}>
                  {emp.email || "-"}
                </TableCell>
                <TableCell sx={{ border: "1px solid #e5e7eb" }}>
                  {emp.phone ? formatPhoneNumber(emp.phone) : "-"}
                </TableCell>
                <TableCell
                  sx={{
                    border: "1px solid #e5e7eb",
                    textAlign: "center",
                    whiteSpace: "nowrap",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      gap: 4,
                    }}
                  >
                    <Tooltip title="View">
                      <IconButton
                        color="info"
                        size="small"
                        onClick={() => {
                          if (user?.role === ROLES.CO_OWNER) {
                            navigate(
                              `/co-owner/customers/${emp.id}`
                            );
                          } else if (user.role === ROLES.EMPLOYEE) {
                            navigate(
                              `/employee/customers/${emp.id}`
                            );
                          } else {
                            navigate(`/customers/${emp.id}`);
                          }
                        }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton
                        color="secondary"
                        size="small"
                        onClick={() => handleEdit(emp)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Deactivate">
                      <IconButton
                        size="small"
                        sx={{ color: "#f59e0b" }}
                        onClick={() => openDeactivateDialog(emp)}
                      >
                        <BlockIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleOpenDialog(emp)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <ConfirmDeleteDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        onConfirm={handleDelete}
        itemName={(selectedCustomer?.firstName + " " + selectedCustomer?.lastName) || ""}
      />

      <ConfirmBlockDialog
        open={openStatusDialog}
        isActive={!!selectedCustomer?.isActive}
        onClose={() => setOpenStatusDialog(false)}
        onConfirm={handleStatusConfirm}
        itemName={(selectedCustomer?.firstName + " " + selectedCustomer?.lastName)}
      />
    </>
  );
};

export default CustomerTableView;
